import { TextButton } from '@creditornot/cb-button';
import { WindowNew } from '@creditornot/cb-icons';
import { cssThemedValue, typographyCss } from '@creditornot/cb-ingredients';
import { MenuItemGroup } from '@creditornot/cb-menu';
import { useDroppable } from '@dnd-kit/core';
import React, { useCallback, useContext } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import styled, { ThemeConsumer } from 'styled-components';

import {
  updateCategoriesOrder,
  updateSubcategoriesOrder,
  changeSubcategoryCategory,
} from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { useViewOnWoltTracking } from 'modules/menu/hooks/telemetryHooks';

import CategoryNavItems from './CategoryNavItems';
import ItemsWithNoCategoryNavItem from './ItemsWithNoCategoryNavItem';
import MenuStatus from './MenuStatus';
import NavItem from './NavItem';
import useMenuLastEdited from './useMenuLastEdited';
import { reportWoltcomLinkClick } from '../behavioral';
import { MenuEditorDragData } from '../components/MenuEditorDnDContext';
import { MenuFeaturesContext } from '../components/MenuFeaturesContext';
import { MenuPermissionsContext } from '../components/MenuPermissionsContext';
import { WOLT_SPECIALS_CATEGORY_ID } from '../items/constants';
import { getItemCategory } from '../items/utils';
import useMenuData from '../useMenuData';

type Props = {
  className?: string;
  hasMenuItemExportImportPermission: boolean;
  language: Language;
  menuId: string;
  woltcomUrl: string;
};

const Root = styled.div`
  padding: 18px 24px 38px;
`;

const AllItemsNavItem = styled(NavItem)``;
const OptionsNavItem = styled(NavItem)``;
const AllDisabledNavItem = styled(NavItem)``;

const Section = styled.div`
  & + & {
    margin-block-start: 40px;
  }
`;

const List = styled(MenuItemGroup)`
  & + & {
    margin-block-start: 5px;
  }
`;

const AddCategory = styled(TextButton)`
  margin-block-start: 12px;
  margin-inline-start: -1px;
`;

const WoltcomLink = styled.a`
  ${typographyCss.Body3};
  font-weight: bold;
  color: ${cssThemedValue('textSecondary')};
`;

const WoltcomLinkIcon = styled(WindowNew)`
  fill: currentcolor;
  height: auto;
  margin-inline-start: 8px;
  position: relative;
  top: 1px;
  vertical-align: baseline;
  width: 12px;
`;

const MenuLastEdited = styled.div`
  ${typographyCss.Body3};
  color: ${cssThemedValue('textSecondary')};
  margin-block-start: 36px;
`;

const useMenuNavigationDroppable = () => {
  const data: MenuEditorDragData = {
    type: 'DND_NAVIGATION_LINKS',
    shrinkOverlayTypes: [
      'DND_EDITOR_SINGLE_ITEM',
      'DND_EDITOR_MULTIPLE_ITEMS',
      'DND_EDITOR_SINGLE_NO_CATEGORY_ITEM',
      'DND_EDITOR_MULTIPLE_NO_CATEGORY_ITEMS',
    ],
  };

  return useDroppable({
    id: 'MenuNavigation',
    data,
  });
};

const MenuNavigation = ({ className, language, menuId, woltcomUrl }: Props) => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const menuPermissions = useContext(MenuPermissionsContext);
  const { categoryImages } = useContext(MenuFeaturesContext);
  const menuLastEdited = useMenuLastEdited();
  const menuNavigationDroppable = useMenuNavigationDroppable();

  const { items, categories, options, updateMenuData } = useMenuData();

  const specialCategory = categories.find(category => category.id === WOLT_SPECIALS_CATEGORY_ID);

  const disabledItems = items.filter(item => !item.enabled);

  const disabledWoltSpecialsCount = specialCategory
    ? disabledItems.filter(item => specialCategory.item_bindings.find(itemBinding => itemBinding.item_id === item.id))
        .length
    : 0;
  const disabledRegularItemsCount = disabledItems.length - disabledWoltSpecialsCount;

  const disabledOptionsCount = options.filter(option => option.values.find(optionValue => !optionValue.enabled)).length;

  const nonCategorisedItemsCount = items.filter(item => !getItemCategory(categories, item.id)).length;

  const allItemsCount = items.length;
  const optionsCount = options.length;
  const specialItemsCount = specialCategory ? specialCategory.item_bindings.length : 0;

  const disabledItemsCountToShow =
    disabledOptionsCount + disabledRegularItemsCount + (menuPermissions.woltSpecials ? disabledWoltSpecialsCount : 0);

  const handleUpdateCategoriesOrder = useCallback(
    async (categories: Category[]) => {
      try {
        const updatedMenu = await updateCategoriesOrder(
          menuId,
          categories.map(category => category.id),
        );
        updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
      } catch (error) {
        toast.error(error.message, { duration: Infinity });
      }
    },
    [updateMenuData, menuId],
  );

  const handleSubCategoriesOrder = useCallback(
    async (categoryId: string, subcategories: Category[]) => {
      try {
        const updatedMenu = await updateSubcategoriesOrder(
          menuId,
          categoryId,
          subcategories.map(category => category.id),
        );
        updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
      } catch (error) {
        toast.error(error.message, { duration: Infinity });
      }
    },
    [updateMenuData, menuId],
  );

  const trackViewOnWolt = useViewOnWoltTracking();
  const handleChangeSubcategoryCategory = useCallback(
    async (subcategoryId: string, categoryId: string, subcategories: Category[]) => {
      try {
        await changeSubcategoryCategory(menuId, subcategoryId, categoryId);

        const updatedMenu = await updateSubcategoriesOrder(
          menuId,
          categoryId,
          subcategories.map(category => category.id),
        );
        updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
      } catch (error) {
        toast.error(error.message, { duration: Infinity });
      }
    },
    [updateMenuData, menuId],
  );

  return (
    <ThemeConsumer>
      {theme => (
        <Root ref={menuNavigationDroppable.setNodeRef} className={className} dir={theme.cbDirection}>
          <MenuStatus />
          <Section>
            <List title={<FormattedMessage id="menu.collections" />}>
              <AllItemsNavItem
                name={<FormattedMessage id="menu.allItems" />}
                count={menuPermissions.woltSpecials ? allItemsCount : allItemsCount - specialItemsCount}
                link={url}
              />
              <OptionsNavItem
                name={<FormattedMessage id="menu.allOptions" />}
                count={optionsCount}
                link={`${url}/options`}
              />
              <AllDisabledNavItem
                name={<FormattedMessage id="menu.allDisabled" />}
                count={disabledItemsCountToShow}
                link={`${url}/all-disabled`}
              />
              <ItemsWithNoCategoryNavItem
                canDrop={itemId =>
                  !specialCategory?.item_bindings?.map(itemBinding => itemBinding.item_id).includes(itemId)
                }
                menuId={menuId}
                name={<FormattedMessage id="menu.nonCategorised" />}
                count={nonCategorisedItemsCount}
                link={`${url}/no-category`}
              />
            </List>
          </Section>
          <Section>
            <CategoryNavItems
              categories={categories}
              menuEditorRootUrl={url}
              hasCategoryImages={categoryImages}
              menuPermissions={menuPermissions}
              language={language}
              onUpdateCategoriesOrder={handleUpdateCategoriesOrder}
              onUpdateSubcategoriesOrder={handleSubCategoriesOrder}
              onChangeSubcategoryCategory={handleChangeSubcategoryCategory}
            />
            {menuPermissions.contentEdit && (
              <AddCategory onClick={() => history.push({ pathname: `${url}/add-categories` })}>
                <FormattedMessage id="menu.addCategory" />
              </AddCategory>
            )}
          </Section>
          <Section>
            <WoltcomLink
              href={woltcomUrl}
              target="blank"
              onClick={() => {
                reportWoltcomLinkClick();
                trackViewOnWolt();
              }}
            >
              <FormattedMessage id="menu.viewOnWoltcom" />
              <WoltcomLinkIcon />
            </WoltcomLink>
          </Section>
          {menuLastEdited && <MenuLastEdited>{menuLastEdited}</MenuLastEdited>}
        </Root>
      )}
    </ThemeConsumer>
  );
};

export default React.memo(MenuNavigation);
